import {ApiClientError, apiRequest, betterAuthRequest} from './client';
import {getSafeReturnTo} from '@/lib/auth-return';
import type {User} from '@/types/api';

function callbackUrl(returnTo: string): string {
    return new URL(getSafeReturnTo(returnTo), window.location.origin).toString();
}

/**
 * Sign-in, sign-up, magic link and sign-out are BetterAuth's; the session is a
 * same-origin cookie rather than a token this app stores.
 *
 * `/auth/me` stays on the control plane because it returns the platform profile
 * — role and status — which BetterAuth's own /get-session does not carry.
 */
export const authApi = {
    async login(email: string, password: string): Promise<User> {
        await betterAuthRequest('/sign-in/email', {email, password});

        return authApi.me();
    },

    async register(email: string, name: string, returnTo = '/dashboard'): Promise<void> {
        await betterAuthRequest('/sign-in/magic-link', {email, name, callbackURL: callbackUrl(returnTo)});
    },

    // Replaces the old password-reset and resend-verification flows: signing in
    // by link both recovers the account and verifies the address.
    async sendSignInLink(email: string, returnTo = '/dashboard'): Promise<void> {
        await betterAuthRequest('/sign-in/magic-link', {email, callbackURL: callbackUrl(returnTo)});
    },

    async startSocialSignIn(provider: 'google' | 'microsoft-entra-id', returnTo = '/'): Promise<void> {
        let res: Response;
        if (provider === 'google') {
            res = await fetch('/api/auth/sign-in/social', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({provider: 'google', callbackURL: callbackUrl(returnTo)})
            });
        } else if (provider === 'microsoft-entra-id') {
            res = await fetch('/api/auth/sign-in/oauth2', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({providerId: 'microsoft-entra-id', callbackURL: callbackUrl(returnTo)})
            });
        } else {
            throw new Error('Invalid provider');
        }

        const data = await res.json();
        if (res.ok && typeof data.url === 'string') {
            window.location.href = data.url;
        } else {
            throw new Error('Social sign-in failed');
        }
    },

    async me(): Promise<User> {
        return apiRequest<User>('/auth/me', {
            method: 'GET'
        });
    },

    async signOut(): Promise<void> {
        try {
            await apiRequest('/auth/end-root-session', {method: 'POST'});
        } catch (error) {
            if (!(error instanceof ApiClientError && error.status === 401)) {
                throw error;
            }
        }
        await betterAuthRequest('/sign-out');
    }
};
